import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { API } from "@/lib/utils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pool-lesson-book/artifacts/c9s2nl0s_rr%20logo.png";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "camp@rollingriver.com",
    password: "",
    confirm_password: "",
  });

  useEffect(() => {
    checkSetup();
  }, []);

  const checkSetup = async () => {
    try {
      const res = await axios.get(`${API}/admin/check-setup`);
      setIsSetup(res.data.password_set);
    } catch (err) {
      console.error("Failed to check setup:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!isSetup) {
        // First-time setup
        if (formData.password !== formData.confirm_password) {
          toast.error("Passwords do not match");
          setSubmitting(false);
          return;
        }
        if (formData.password.length < 8) {
          toast.error("Password must be at least 8 characters");
          setSubmitting(false);
          return;
        }
        await axios.post(`${API}/admin/setup-password`, {
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirm_password,
        });
        toast.success("Password set successfully!");
        localStorage.setItem("admin_authenticated", "true");
        navigate("/admin/dashboard");
      } else {
        // Login
        const res = await axios.post(`${API}/admin/login`, {
          email: formData.email,
          password: formData.password,
        });
        if (res.data.authenticated) {
          localStorage.setItem("admin_authenticated", "true");
          navigate("/admin/dashboard");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-16 h-16 border-4 border-[#109FE9] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Rolling River Day Camp" className="h-24 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-slate-800">
            Admin Portal
          </h1>
          <p className="text-slate-600 mt-1">
            {isSetup ? "Sign in to manage swim lessons" : "Set up your admin password"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-6">
          <div>
            <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
              <Mail className="w-4 h-4 inline mr-1" /> Email
            </Label>
            <Input
              data-testid="admin-email"
              type="email"
              className="form-input w-full bg-slate-100"
              value={formData.email}
              disabled
            />
            <p className="text-xs text-slate-500 mt-1">
              Admin access is restricted to camp@rollingriver.com
            </p>
          </div>

          <div>
            <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
              <Lock className="w-4 h-4 inline mr-1" /> Password
            </Label>
            <div className="relative">
              <Input
                data-testid="admin-password"
                type={showPassword ? "text" : "password"}
                className="form-input w-full pr-10"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={isSetup ? "Enter password" : "Create password (min 8 characters)"}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {!isSetup && (
            <div>
              <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                <Lock className="w-4 h-4 inline mr-1" /> Confirm Password
              </Label>
              <Input
                data-testid="admin-confirm-password"
                type={showPassword ? "text" : "password"}
                className="form-input w-full"
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                placeholder="Confirm password"
                required
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full"
            data-testid="admin-login-btn"
          >
            {submitting ? "Please wait..." : isSetup ? "Sign In" : "Set Password & Continue"}
          </Button>
        </form>

        <p className="text-center text-sm text-slate-500 mt-6">
          <a href="/" className="text-[#109FE9] hover:underline">
            ← Back to Registration
          </a>
        </p>
      </div>
    </div>
  );
}
